import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XClose } from '@untitled-ui/icons-react';
import {
  type CSSProperties,
  type ComponentRef,
  Fragment,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { isMobile } from '../shared/mobile';
import { ButtonCol } from './buttons/button-col';
import { ButtonsRow } from './buttons/buttons-row';
import { IconButton } from './buttons/icon-button';
import { DotStepper, type DotStepperProps } from './indicators/dot-stepper';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;

function getViewportResizeValues() {
  if (window.visualViewport === null || window.visualViewport === undefined) {
    return;
  }

  const viewport = window.visualViewport;
  const windowHeight = window.innerHeight;
  const viewportHeight = viewport.height;
  const offset = windowHeight - viewportHeight;

  return { keyboardOffset: Math.max(0, offset), availableHeight: viewportHeight };
}

export const DialogOverlay = forwardRef<
  ComponentRef<typeof DialogPrimitive.Overlay>,
  DialogPrimitive.DialogOverlayProps
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={twMerge([
        'fixed inset-0 z-50 bg-black/50',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      ])}
      {...props}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

export function DialogHeader(props: { children: ReactNode; className?: string }) {
  return <div className={twMerge('flex-shrink-0', props.className ?? '')}>{props.children}</div>;
}

export function DialogTitle(
  props: DialogPrimitive.DialogTitleProps & { align?: 'left' | 'center' | 'right' }
) {
  const alignmentStyle = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[props.align ?? 'left'];
  return (
    <DialogPrimitive.Title
      className={twMerge('font-display-3 w-full px-5', alignmentStyle, props.className ?? '')}
    >
      {props.children}
    </DialogPrimitive.Title>
  );
}

export function DialogDescription(
  props: DialogPrimitive.DialogDescriptionProps & { align?: 'left' | 'center' | 'right' }
) {
  const alignmentStyle = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[props.align ?? 'left'];

  const content =
    typeof props.children === 'string' && props.children.includes('\n')
      ? props.children.split('\n').map((line, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </Fragment>
        ))
      : props.children;

  return (
    <DialogPrimitive.Description
      className={twMerge('font-body-2-r text-branding-gray', alignmentStyle, props.className ?? '')}
    >
      {content}
    </DialogPrimitive.Description>
  );
}

export type DialogContentProps = {
  hasCloseButton?: boolean;
  closeButtonHasShadow?: boolean;
  /** When true, the dialog will be 80% of viewport height with scrollable body */
  fixedHeight?: boolean;
};

export const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  DialogPrimitive.DialogContentProps & DialogContentProps
>(function DialogContent(props, ref) {
  const {
    className = '',
    children,
    hasCloseButton = false,
    closeButtonHasShadow = false,
    fixedHeight = false,
    ...dialogProps
  } = props;

  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [availableHeight, setAvailableHeight] = useState(0);
  const isMobileDevice = isMobile();

  useEffect(function () {
    if (
      typeof window === 'undefined' ||
      window.visualViewport === null ||
      window.visualViewport === undefined
    ) {
      return;
    }

    const viewportResizeValues = getViewportResizeValues();
    if (viewportResizeValues == null) {
      return;
    }

    const { keyboardOffset, availableHeight } = viewportResizeValues;
    setKeyboardOffset(keyboardOffset);
    setAvailableHeight(availableHeight);
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay className="bg-black opacity-25" />
      <DialogPrimitive.Content
        onOpenAutoFocus={function (e) {
          if (isMobileDevice) {
            e.preventDefault();
          }
        }}
        ref={ref}
        style={{
          ['--keyboard-offset' as string]: `${keyboardOffset}px`,
          ['--available-height' as string]:
            availableHeight > 0 ? `${availableHeight * 0.9}px` : '85vh',
        }}
        className={twMerge([
          'fixed z-50 w-[480px] max-w-lg rounded-xl bg-white p-6 shadow-3 outline-none',
          'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
          'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          'md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-xl',
          'max-md:inset-x-0 max-md:top-auto max-md:w-full max-md:max-w-full max-md:translate-x-0 max-md:translate-y-0',
          'max-md:bottom-[var(--keyboard-offset)] max-md:transition-[bottom] max-md:duration-300 max-md:ease-out',
          'max-md:rounded-b-none max-md:rounded-t-2xl',
          'max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom',
          'max-md:data-[state=closed]:slide-out-to-left-0 max-md:data-[state=closed]:slide-out-to-top-0',
          'max-md:data-[state=open]:slide-in-from-left-0 max-md:data-[state=open]:slide-in-from-top-0',
          fixedHeight ? 'flex max-h-[90vh] flex-col' : 'max-h-full',
          'max-md:flex max-md:max-h-[var(--available-height)] max-md:flex-col max-md:overflow-hidden',
          className,
        ])}
        {...(hasCloseButton
          ? {
              ...dialogProps,
              onEscapeKeyDown: function (event) {
                event.preventDefault();
              },
              onPointerDownOutside: function (event) {
                event.preventDefault();
              },
            }
          : dialogProps)}
      >
        {hasCloseButton ? (
          <DialogPrimitive.DialogClose
            className={twMerge(
              'absolute right-2 top-2 z-10',
              closeButtonHasShadow ? 'outline outline-8 outline-white' : ''
            )}
            asChild
          >
            <IconButton icon={XClose} variant="outline-circular" tabIndex={-1} size="md" />
          </DialogPrimitive.DialogClose>
        ) : null}
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

export const DialogBody = forwardRef<HTMLDivElement, { children: ReactNode; className?: string }>(
  function DialogBody(props, ref) {
    return (
      <div ref={ref} className={twMerge('min-h-0 flex-1 overflow-y-auto', props.className ?? '')}>
        {props.children}
      </div>
    );
  }
);
DialogBody.displayName = 'DialogBody';

export function DialogStepper(props: DotStepperProps) {
  return (
    <div className="flex items-center justify-center py-8">
      <DotStepper {...props} />
    </div>
  );
}

export function DialogFooter(props: {
  children: ReactNode;
  className?: string;
  isColumn?: boolean;
  style?: CSSProperties;
}) {
  if (props.isColumn) {
    return (
      <ButtonCol
        className={twMerge('mt-8 w-full flex-shrink-0 gap-3', props.className ?? '')}
        style={props.style}
      >
        {props.children}
      </ButtonCol>
    );
  }

  return (
    <ButtonsRow
      className={twMerge('mt-8 w-full flex-shrink-0 gap-3', props.className ?? '')}
      style={props.style}
    >
      {props.children}
    </ButtonsRow>
  );
}

export function DialogImage(props: { src: string; alt: string; className?: string }) {
  return (
    <div
      className={twMerge(
        'relative h-[243px] w-full overflow-hidden rounded-lg',
        props.className ?? ''
      )}
    >
      <img
        className="absolute left-1/2 top-1/2 h-auto w-full -translate-x-1/2 -translate-y-1/2"
        src={props.src}
        alt={props.alt}
      />
    </div>
  );
}
